import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  effect,
  input,
  output,
  signal,
  viewChild,
} from "@angular/core";
import { SwitchIcons } from "../../icon/switch-icons/switch-icons";
import { ThemeToggle } from "../../form/theme-toggle/theme-toggle";
import { NavItem } from "../../../core/layout/header-nav.service";

/** Deve casar com a duração de `navmenu-panel-out`. */
const CLOSE_ANIMATION_MS = 280;

/**
 * Menu de navegação global — painel sobreposto (dumb) que reproduz os destinos
 * do dashboard como botões de texto + ícone empilhados na vertical.
 *
 * Dumb: não injeta Router nem Auth. Recebe `items`/`open`/`isLoggedIn` via input()
 * e emite `(select)`, `(close)` e `(auth)`; o header (smart) decide o que fazer.
 *
 * Herda a linguagem visual dos modais (backdrop `bg-black/70 backdrop-blur-sm`) e
 * usa keyframes *scoped* para a transição — o app não tem plugin de animação
 * (as classes `animate-in`/`slide-in-*` existentes são inertes), então o
 * componente traz o seu próprio movimento, respeitando `prefers-reduced-motion`.
 */
@Component({
  selector: "app-nav-menu",
  standalone: true,
  imports: [SwitchIcons, ThemeToggle],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (mounted()) {
    <div
      class="fixed inset-0 z-40"
      role="dialog"
      aria-modal="true"
      aria-label="Menu de navegação"
      (keydown)="onKeydown($event)"
    >
      <!-- Backdrop com blur (padrão dos modais) -->
      <div
        class="absolute inset-0 bg-black/70 backdrop-blur-sm navmenu-backdrop"
        [class.is-closing]="closing()"
        (click)="close.emit()"
      ></div>

      <!-- Painel (drawer à direita) -->
      <nav
        #panel
        class="absolute top-0 right-0 h-full w-[min(20rem,85vw)] backdrop-blur-md border-l border-[var(--bf-chrome-border)] text-[var(--bf-chrome-text)] shadow-2xl flex flex-col navmenu-panel"
        [class.is-closing]="closing()"
      >
        <div
          class="flex items-center justify-between px-5 py-4 border-b border-[var(--bf-chrome-border)]"
        >
          <span class="font-playfair text-lg text-gradient-gold">Navegação</span>
          <button
            type="button"
            (click)="close.emit()"
            aria-label="Fechar menu"
            data-autofocus
            class="opacity-80 hover:opacity-100 transition-opacity duration-200"
          >
            <app-switch-icons icon="close" size="6" />
          </button>
        </div>

        <div class="flex-1 overflow-y-auto flex flex-col gap-1 p-3">
          @for (item of items(); track item.label) {
          <button
            type="button"
            [disabled]="item.disabled"
            (click)="onSelect(item)"
            class="w-full flex items-center gap-3 text-left rounded-xl px-4 py-3 min-h-[44px] transition-all duration-200 hover:bg-[var(--bf-hover)] active:scale-[.98] disabled:opacity-40 disabled:pointer-events-none"
          >
            <span
              class="bf-chip-gold w-9 h-9 shrink-0 rounded-lg flex items-center justify-center"
            >
              <app-switch-icons [icon]="item.icon" size="5" />
            </span>
            <span class="font-semibold">{{ item.label }}</span>
            @if (item.disabled) {
            <span
              class="ml-auto text-[10px] uppercase tracking-wide rounded-full px-2 py-1 border border-[var(--bf-neutral-border)] bg-[var(--bf-neutral-bg)] text-[var(--bf-neutral-text)]"
              >Em breve</span
            >
            }
          </button>
          }
        </div>

        <div class="p-3 border-t border-[var(--bf-chrome-border)]">
          <app-theme-toggle [dark]="dark()" (toggle)="themeToggle.emit()" />
        </div>

        <div class="p-3 border-t border-[var(--bf-chrome-border)]">
          <!--
            Spec 026: Logout é ação destrutiva — outline vermelho nos dois temas
            (texto e borda de perigo, fundo só um tingido no hover). Login segue
            o dourado de marca.
          -->
          <button
            type="button"
            (click)="auth.emit()"
            class="w-full flex items-center justify-center gap-2 rounded-xl px-4 py-3 min-h-[44px] font-bold transition-all duration-200"
            [class]="
              isLoggedIn()
                ? 'border border-[var(--bf-danger-border)] text-[var(--bf-danger-text)] bg-transparent hover:bg-[var(--bf-danger-bg)]'
                : 'bf-cta-gold hover:scale-[1.02]'
            "
          >
            {{ isLoggedIn() ? "Logout" : "Login" }}
          </button>
        </div>
      </nav>
    </div>
    }
  `,
  styles: [
    `
      @keyframes navmenu-backdrop-in {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      @keyframes navmenu-panel-in {
        from {
          opacity: 0;
          transform: translateX(1.5rem);
        }
        to {
          opacity: 1;
          transform: none;
        }
      }
      /* Saída espelha a entrada, no mesmo tempo e curva (spec 011 RF17). */
      @keyframes navmenu-backdrop-out {
        from {
          opacity: 1;
        }
        to {
          opacity: 0;
        }
      }
      @keyframes navmenu-panel-out {
        from {
          opacity: 1;
          transform: none;
        }
        to {
          opacity: 0;
          transform: translateX(1.5rem);
        }
      }
      .navmenu-backdrop {
        animation: navmenu-backdrop-in 0.2s ease-out both;
      }
      /*
       * Spec 026: o drawer é chrome e segue o tema por inteiro, como o header —
       * vidro branco com texto roxo no claro, roxo profundo com texto claro no
       * escuro. Revoga a regra antiga ("roxo profundo nos dois temas"): metade
       * do componente lia token e metade não, e no claro a borda virava branca
       * e o Logout, texto branco sobre branco.
       *
       * O painel é mais opaco que o --bf-chrome-bg do header porque flutua
       * sobre o backdrop escuro do menu: com 72% de branco ele viraria cinza.
       * Os chips dourados dos itens são marca e ficam iguais nos dois temas.
       */
      .navmenu-panel {
        background-color: color-mix(in srgb, var(--bf-surface) 92%, transparent);
        animation: navmenu-panel-in 0.28s cubic-bezier(0.16, 1, 0.3, 1) both;
      }
      .navmenu-backdrop.is-closing {
        animation: navmenu-backdrop-out 0.2s ease-in both;
      }
      .navmenu-panel.is-closing {
        animation: navmenu-panel-out 0.28s cubic-bezier(0.7, 0, 0.84, 0) both;
      }
      @media (prefers-reduced-motion: reduce) {
        .navmenu-backdrop,
        .navmenu-panel,
        .navmenu-backdrop.is-closing,
        .navmenu-panel.is-closing {
          animation: none;
        }
      }
    `,
  ],
})
export class NavMenu implements OnDestroy {
  open = input.required<boolean>();
  items = input.required<NavItem[]>();
  isLoggedIn = input<boolean>(false);
  dark = input<boolean>(false);

  select = output<NavItem>();
  close = output<void>();
  auth = output<void>();
  themeToggle = output<void>();

  /**
   * `mounted` sobrevive ao `open` virar falso pelo tempo da animação de saída
   * — sem isso o `@if` arrancaria o painel do DOM e o fechamento não teria
   * movimento nenhum (spec 011 RF17).
   */
  readonly mounted = signal(false);
  readonly closing = signal(false);
  private closeTimer?: ReturnType<typeof setTimeout>;

  private readonly panel = viewChild<ElementRef<HTMLElement>>("panel");

  constructor() {
    effect(() => {
      const isOpen = this.open();
      // Scroll-lock no body + foco inicial no painel enquanto aberto.
      document.body.style.overflow = isOpen ? "hidden" : "";

      if (isOpen) {
        clearTimeout(this.closeTimer);
        this.closing.set(false);
        this.mounted.set(true);
        setTimeout(() => {
          const el = this.panel()?.nativeElement;
          el?.querySelector<HTMLElement>("[data-autofocus]")?.focus();
        });
        return;
      }

      if (!this.mounted()) return;
      this.closing.set(true);
      this.closeTimer = setTimeout(() => {
        this.mounted.set(false);
        this.closing.set(false);
      }, prefersReducedMotion() ? 0 : CLOSE_ANIMATION_MS);
    });
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      this.close.emit();
      return;
    }
    if (event.key !== "Tab") return;

    // Foco preso: cicla entre o primeiro e o último elemento focável do painel.
    const focusables = this.getFocusables();
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement as HTMLElement | null;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  onSelect(item: NavItem): void {
    if (!item.disabled) this.select.emit(item);
  }

  private getFocusables(): HTMLElement[] {
    const el = this.panel()?.nativeElement;
    if (!el) return [];
    return Array.from(
      el.querySelectorAll<HTMLElement>("button:not([disabled]), a[href]"),
    );
  }

  ngOnDestroy(): void {
    clearTimeout(this.closeTimer);
    document.body.style.overflow = "";
  }
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  );
}
